(() => {
  const ROOT_SELECTOR = "[data-wool-lp]";

  const initializeMotion = (root) => {
    const motion = root.querySelector("[data-product-motion]");
    if (!motion || motion.dataset.motionInitialized === "true") return;

    const frames = [...motion.querySelectorAll("[data-motion-frame]")];
    const buttons = [...motion.querySelectorAll("[data-motion-button]")];
    const toggle = motion.querySelector("[data-motion-toggle]");
    if (!frames.length || frames.length !== buttons.length) return;

    motion.dataset.motionInitialized = "true";

    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)");
    let activeIndex = 0;
    let intervalId = null;
    let isInViewport = false;
    let isPaused = reducedMotion.matches;

    const updateToggle = () => {
      if (!toggle) return;
      toggle.textContent = isPaused ? "再生" : "一時停止";
      toggle.setAttribute("aria-pressed", String(isPaused));
    };

    const showFrame = (nextIndex, moveFocus = false) => {
      activeIndex = (nextIndex + frames.length) % frames.length;

      frames.forEach((frame, index) => {
        const isActive = index === activeIndex;
        frame.classList.toggle("is-active", isActive);
        frame.setAttribute("aria-hidden", String(!isActive));
      });

      buttons.forEach((button, index) => {
        const isActive = index === activeIndex;
        button.setAttribute("aria-selected", String(isActive));
        button.tabIndex = isActive ? 0 : -1;
      });

      if (moveFocus) buttons[activeIndex]?.focus();
    };

    const stop = () => {
      if (!intervalId) return;
      window.clearInterval(intervalId);
      intervalId = null;
    };

    const start = () => {
      stop();
      if (isPaused || !isInViewport || document.hidden) return;
      intervalId = window.setInterval(() => {
        showFrame(activeIndex + 1);
      }, 3600);
    };

    buttons.forEach((button, index) => {
      button.addEventListener("click", () => {
        showFrame(index);
        start();
      });

      button.addEventListener("keydown", (event) => {
        let nextIndex = null;

        if (event.key === "ArrowRight") nextIndex = activeIndex + 1;
        if (event.key === "ArrowLeft") nextIndex = activeIndex - 1;
        if (event.key === "Home") nextIndex = 0;
        if (event.key === "End") nextIndex = frames.length - 1;
        if (nextIndex === null) return;

        event.preventDefault();
        showFrame(nextIndex, true);
        start();
      });
    });

    toggle?.addEventListener("click", () => {
      isPaused = !isPaused;
      updateToggle();
      if (isPaused) {
        stop();
      } else {
        start();
      }
    });

    document.addEventListener("visibilitychange", () => {
      if (document.hidden) {
        stop();
      } else {
        start();
      }
    });

    reducedMotion.addEventListener?.("change", (event) => {
      isPaused = event.matches;
      updateToggle();
      if (isPaused) {
        stop();
      } else {
        start();
      }
    });

    if ("IntersectionObserver" in window) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          isInViewport = entry.isIntersecting;
          if (isInViewport) {
            start();
          } else {
            stop();
          }
        },
        { threshold: 0.35 },
      );
      observer.observe(motion);
    } else {
      isInViewport = true;
    }

    showFrame(0);
    updateToggle();
    start();
  };

  const initialize = (root) => {
    if (!root || root.dataset.woolInitialized === "true") return;
    root.dataset.woolInitialized = "true";
    initializeMotion(root);

    const purchase = root.querySelector("[data-wool-purchase]");
    if (!purchase) return;

    const form = purchase.querySelector(".ys-wool-lp__product-form");
    const lengthOptions = [...purchase.querySelectorAll("[data-length-option]")];
    const widthOptions = [...purchase.querySelectorAll("[data-width-option]")];
    const widthWraps = [...purchase.querySelectorAll("[data-width-option-wrap]")];
    const variantInput = purchase.querySelector("[data-variant-id]");
    const price = purchase.querySelector("[data-variant-price]");
    const selectedSize = purchase.querySelector("[data-selected-size]");
    const selectedFill = purchase.querySelector("[data-selected-fill]");
    const availability = purchase.querySelector("[data-availability]");
    const addButton = purchase.querySelector("[data-add-to-cart]");
    const stickyStatus = root.querySelector("[data-sticky-status]");
    const stickyAction = root.querySelector("[data-sticky-action]");
    const initialPrice = price?.textContent.trim() || "";
    const initialStickyStatus = stickyStatus?.textContent.trim() || "";

    if (variantInput) {
      variantInput.disabled = false;
    }

    const clearSelection = (length) => {
      widthOptions.forEach((option) => {
        option.checked = false;
      });

      if (variantInput) variantInput.value = "";
      if (price) price.textContent = initialPrice;
      if (selectedSize) {
        selectedSize.textContent = `丈${length}cmを選択中。幅を選んでください。`;
      }
      if (selectedFill) selectedFill.textContent = "—";
      if (availability) availability.textContent = "幅を選ぶと、カートへ追加できます。";
      if (addButton) addButton.disabled = true;
      if (stickyStatus) stickyStatus.textContent = initialStickyStatus;
      if (stickyAction) stickyAction.textContent = "サイズ・価格を見る";
    };

    const showLength = (length) => {
      widthWraps.forEach((wrap) => {
        const visible = wrap.dataset.length === length;
        wrap.hidden = !visible;

        const option = wrap.querySelector("[data-width-option]");
        if (option) {
          option.disabled = !visible || option.dataset.available !== "true";
        }
      });

      clearSelection(length);
    };

    const applyVariant = (option) => {
      if (!option) return;

      const available = option.dataset.available === "true";
      if (variantInput) variantInput.value = available ? option.value : "";
      if (price) price.textContent = option.dataset.price || initialPrice;
      if (selectedSize) selectedSize.textContent = option.dataset.title || "";
      if (selectedFill) selectedFill.textContent = option.dataset.fill || "—";
      if (addButton) addButton.disabled = !available;

      if (availability) {
        availability.textContent = available
          ? "選択したサイズをカートへ追加できます。"
          : "このサイズは現在売り切れです。";
      }

      if (stickyStatus) {
        stickyStatus.textContent = available
          ? `選択中 ${option.dataset.title}`
          : initialStickyStatus;
      }
      if (stickyAction) {
        stickyAction.textContent = available
          ? `${option.dataset.price}で購入へ`
          : "サイズ・価格を見る";
      }
    };

    lengthOptions.forEach((option) => {
      option.addEventListener("change", () => {
        if (option.checked) showLength(option.value);
      });
    });

    widthOptions.forEach((option) => {
      option.addEventListener("change", () => {
        if (option.checked) applyVariant(option);
      });
    });

    form?.addEventListener("submit", (event) => {
      const selectedOption = widthOptions.find((option) => option.checked);
      const canSubmit =
        Boolean(variantInput?.value) &&
        selectedOption?.dataset.available === "true" &&
        !selectedOption.disabled;

      if (canSubmit) return;

      event.preventDefault();
      if (availability) {
        availability.textContent = "丈と幅を選んでください。";
      }

      const widthGroup = purchase.querySelector("[data-width-group]");
      widthGroup?.scrollIntoView({ behavior: "smooth", block: "center" });
      widthGroup?.querySelector('input:not([disabled])')?.focus();
    });

    const initialLength =
      lengthOptions.find((option) => option.checked)?.value ||
      lengthOptions[0]?.value ||
      "200";
    showLength(initialLength);
  };

  const initializeAll = (scope = document) => {
    scope.querySelectorAll(ROOT_SELECTOR).forEach(initialize);
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initializeAll(), { once: true });
  } else {
    initializeAll();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initializeAll(event.target);
  });
})();
